import React, { useEffect, useMemo, useRef, useState } from 'react'

// =========================================================================
// TRADUCTOR VISUAL (Para los textos de Ganador en Pantalla)
// =========================================================================
const getVisualZoneName = (realZoneName) => {
    if (!realZoneName) return '';
    const normalized = realZoneName.toLowerCase().trim();

    // Normal
    // const aliases = {
    //     'laboratorio tequis': 'Lucho', 'canels': 'Lucho', 'seguros el potosi': 'Lucho', 'nissan': 'Lucho', 'lounge ron potosi': 'Lucho', 'azul': 'Lucho',
    //     'red cola': 'Tunita', 'tecate': 'Tunita', 'moctezuma': 'Tunita', 'mobil': 'Tunita', 'oro': 'Tunita'
    // };

    // Copa Escuelas ADSL
    const aliases = {
        'team lucho': 'Team Lucho', 'team tunita': 'Team Tunita'
    };

    // Daikin
    // const aliases = {
    //     'laboratorio tequis': 'Pichon-kun', 'canels': 'Pichon-kun', 'seguros el potosi': 'Pichon-kun', 'nissan': 'Pichon-kun', 'lounge ron potosi': 'Pichon-kun', 'azul': 'Pichon-kun',
    //     'red cola': 'Tunita', 'tecate': 'Tunita', 'moctezuma': 'Tunita', 'mobil': 'Tunita', 'oro': 'Tunita'
    // };

    return aliases[normalized] || realZoneName;
};

const GoalScreenV2 = ({ currentMatchState }) => {
    const {
        phase = 'WAITING',
        elapsedMilliseconds = 0,
        totalDurationMilliseconds = 1,
        zonesProgress = [],
        winner = null
    } = currentMatchState || {};

    const [viewStage, setViewStage] = useState('RACING');
    const maxPositionsHistoryRef = useRef({});

    useEffect(() => {
        const currentPhase = phase.toUpperCase();

        if (currentPhase === 'WAITING' || currentPhase === 'IDLE') {
            maxPositionsHistoryRef.current = {};

        } else if (currentPhase === 'RUNNING') {
            setViewStage('RACING');

        } else if (currentPhase === 'FINISHED') {
            const isSingleWinner = winner && winner !== 'tie' && !winner.startsWith('tie_partial:');

            if (isSingleWinner) {
                setViewStage('KICK_ANIMATION');
                setTimeout(() => {
                    setViewStage('WINNER_TEXT');
                }, 2000);
            } else {
                setViewStage('WINNER_TEXT');
            }
        }
    }, [phase, winner]);

    // =========================================================================
    // AGRUPACIÓN INTELIGENTE: Consolida todas las zonas zombis en solo 2 carriles
    // =========================================================================
    const dynamicZonesToRender = useMemo(() => {
        if (!zonesProgress || zonesProgress.length === 0) return [];
        
        const grouped = {};
        
        zonesProgress.forEach(z => {
            const rawZone = z.zoneIdentifier || z.zone || 'unknown';
            const teamName = getVisualZoneName(rawZone); // Retorna "Lucho" o "Tunita"
            
            if (!grouped[teamName]) {
                grouped[teamName] = {
                    zoneIdentifier: teamName, // Usamos el equipo como ID único del carril
                    totalTaps: 0
                };
            }
            // Sumamos los taps de todos los que pertenezcan a este equipo
            grouped[teamName].totalTaps += (z.totalTaps || 0);
        });

        // Convertimos a array y ordenamos (Lucho siempre quedará arriba, Tunita abajo)
        return Object.values(grouped).sort((a, b) => a.zoneIdentifier.localeCompare(b.zoneIdentifier));
    }, [zonesProgress]);

    const timeCompletedPercentage = useMemo(() => {
        if (phase.toUpperCase() === 'WAITING' || phase.toUpperCase() === 'IDLE') return 0;
        const percentage = (elapsedMilliseconds / totalDurationMilliseconds) * 100;
        return Math.min(100, Math.max(0, percentage));
    }, [elapsedMilliseconds, totalDurationMilliseconds, phase]);

    // 1. Calculamos al VERDADERO líder de la carrera usando nuestras zonas ya agrupadas
    const highestTapsOverall = useMemo(() => {
        if (!dynamicZonesToRender || dynamicZonesToRender.length === 0) return 0;
        return Math.max(...dynamicZonesToRender.map(z => z.totalTaps || 0));
    }, [dynamicZonesToRender]);

    const MAXIMUM_VISUAL_LAG_PERCENTAGE = 25; // Máximo rezago visual (25%)
    const CLIMAX_TRIGGER_PERCENTAGE = 75; // Porcentaje donde inicia el sprint final / clímax
    const MAX_LOSER_CLIMAX_POSITION = 82; // Límite máximo para el perdedor en la meta (garantiza ventaja visual)
    const MIN_LEAD_SEPARATION_PERCENTAGE = 4; // Ventaja visual mínima garantizada para el líder

    // Al haber agrupado, nuestros IDs ahora son literalmente "lucho" y "tunita", el diccionario se reduce a esto:
    const ZONES_SKINS = {
        'lucho': { avatar: '/img/games/runtogoal/copa-academias-adsl/lucho.png', spark: '/img/games/runtogoal/copa-academias-adsl/destellos-blancos.png' },
        'tunita': { avatar: '/img/games/runtogoal/copa-academias-adsl/tunita.png', spark: '/img/games/runtogoal/copa-academias-adsl/destellos-rojos.png' },
        'default': { avatar: '/img/games/runtogoal/copa-academias-adsl/lucho.png', spark: '/img/games/runtogoal/copa-academias-adsl/destellos-blancos.png' }
    };

    // =========================================================================
    // HELPER PARA DETERMINAR IMAGEN DEL GANADOR
    // =========================================================================
    const getWinnerImage = (winnerRaw) => {
        if (!winnerRaw) return "/img/games/runtogoal/copa-academias-adsl/lucho-meta.png"; // Fallback por defecto
        
        const visualName = getVisualZoneName(winnerRaw).toUpperCase();
        
        if (visualName === 'TUNITA') {
            return "/img/games/runtogoal/copa-academias-adsl/tunita-meta.png";
        }
        
        return "/img/games/runtogoal/copa-academias-adsl/lucho-meta.png";
    };

    const renderWinnerTextScreen = (winnerRaw) => {
        if (winnerRaw === 'tie') return '¡ES UN EMPATE!';
        if (winnerRaw.startsWith('tie_partial:')) {
            const tiedZones = winnerRaw.split(':')[1].split(' y ');
            const uniqueAliases = [...new Set(tiedZones.map(z => getVisualZoneName(z).toUpperCase()))];
            return `¡HAN EMPATADO ${uniqueAliases.join(' Y ')}!`;
        }
        return `¡GOOOOOL DE TEAM ${getVisualZoneName(winnerRaw).toUpperCase()}!`;
        // return `¡HA GANADO ${getVisualZoneName(winnerRaw).toUpperCase()}!`;
    };

    return (
        <>
            <div className='GoalScreenX__Sponsor' style={{ backgroundColor: '#fff', borderRadius: '8px', opacity: '0.8', padding: '6px', marginTop: '12px' }}>
                <img src="/img/page/adsl-five-bicolor.png" className='GoalScreenX__Sponsor__Image' style={{ width: '100%', maxWidth: '350px' }} alt="Sponsor" />
            </div>

            <div className="goal">
                <div className="goal__pitch Goal__V2__Background"></div>

                <div className="goal__lanes" style={{ gridTemplateRows: `repeat(${dynamicZonesToRender.length}, 1fr)` }}>
                    {dynamicZonesToRender.map((zoneItem, index) => {
                        const zoneId = zoneItem.zoneIdentifier || `unknown-${index}`;
                        
                        // 2. MATEMÁTICA CORREGIDA (Avance suave basado en el líder real)
                        let finalPosition = timeCompletedPercentage;

                        const currentTaps = zoneItem.totalTaps || 0;
                        const isLeader = highestTapsOverall > 0 && currentTaps === highestTapsOverall;

                        if (highestTapsOverall > 0) {
                            const tapsDeficit = highestTapsOverall - currentTaps;

                            if (tapsDeficit > 0) {
                                // Curva no lineal (raíz cuadrada): hace que pequeñas diferencias de taps sean perceptibles
                                const rawRatio = tapsDeficit / highestTapsOverall;
                                const curvedRatio = Math.sqrt(rawRatio);

                                // Rezago base con piso mínimo de separación visual
                                let visualLag = Math.max(MIN_LEAD_SEPARATION_PERCENTAGE, curvedRatio * MAXIMUM_VISUAL_LAG_PERCENTAGE);

                                // Compensación de perspectiva si el carril inferior (index > 0) va perdiendo,
                                // para evitar que la inclinación de la cancha lo haga ver adelante del líder superior
                                if (index > 0) {
                                    visualLag += 3;
                                }

                                // El líder va con el reloj (0 deficit). El que va perdiendo se retrasa visualmente.
                                finalPosition -= visualLag;
                            }
                        }

                        // SPRINT FINAL / CLÍMAX: A partir del 75% antes de llegar a la meta,
                        // el ganador se adelanta con fuerza y el perdedor queda claramente atrás
                        if (timeCompletedPercentage >= CLIMAX_TRIGGER_PERCENTAGE && highestTapsOverall > 0) {
                            const climaxProgress = (timeCompletedPercentage - CLIMAX_TRIGGER_PERCENTAGE) / (100 - CLIMAX_TRIGGER_PERCENTAGE); // 0 a 1

                            if (isLeader) {
                                // El líder avanza con fuerza hacia la meta (100%)
                                finalPosition = timeCompletedPercentage;
                            } else {
                                // El perdedor sufre una separación progresiva adicional y se topa para no alcanzar al líder
                                const climaxSeparation = climaxProgress * 8;
                                finalPosition -= climaxSeparation;

                                if (finalPosition > MAX_LOSER_CLIMAX_POSITION) {
                                    finalPosition = MAX_LOSER_CLIMAX_POSITION;
                                }
                            }
                        }

                        // 3. EVITAR QUE CAMINEN PARA ATRÁS
                        if (phase.toUpperCase() === 'RUNNING') {
                            const previousMaxPosition = maxPositionsHistoryRef.current[zoneId] || 0;
                            if (finalPosition < previousMaxPosition) {
                                finalPosition = previousMaxPosition;
                            } else {
                                maxPositionsHistoryRef.current[zoneId] = finalPosition;
                            }
                        } else if (phase.toUpperCase() === 'FINISHED') {
                            // En finalización, si hay un ganador definido, aseguramos que el ganador quede al 100% y el perdedor atrás
                            const isSingleWinner = winner && winner !== 'tie' && !winner.startsWith('tie_partial:');
                            if (isSingleWinner) {
                                const visualWinnerName = getVisualZoneName(winner).toLowerCase();
                                const currentZoneVisual = (zoneItem.zoneIdentifier || '').toLowerCase();
                                if (currentZoneVisual === visualWinnerName) {
                                    finalPosition = 100;
                                } else {
                                    finalPosition = Math.min(maxPositionsHistoryRef.current[zoneId] || finalPosition, MAX_LOSER_CLIMAX_POSITION);
                                }
                            } else {
                                finalPosition = maxPositionsHistoryRef.current[zoneId] || finalPosition;
                            }
                        } else if (phase.toUpperCase() === 'WAITING' || phase.toUpperCase() === 'IDLE') {
                            finalPosition = 0;
                        }

                        finalPosition = Math.min(100, Math.max(0, finalPosition));
                        const safeZoneId = (zoneId || '').trim().toLowerCase();
                        const skin = ZONES_SKINS[safeZoneId] || ZONES_SKINS["default"];

                        // 3. CORRECCIÓN DE PERSPECTIVA DE LA CANCHA
                        // Ajuste de Perspectiva: Mueve SOLO LA META. 
                        // El carril superior frena a 20vw del borde. El inferior frena a 14vw (para igualar la portería inclinada).
                        // Al estar en 0%, ambos arrancan desde la misma línea exacta.
                        const finishGap = 20 - (index * 6); // Tiro a gol
                        // const finishGap = 20 - (index * 5);  // Carrera Atleti

                        return (
                            <div key={zoneId} className="goal__lane" style={{ '--finish-gap': `${finishGap}vw` }}>
                                <div className="goal__runner" style={{ '--pct': finalPosition / 100 }}>
                                    <img className="goal__velocity" src={skin.spark} alt="destellos" />
                                    <img className="goal__avatar" src={skin.avatar} alt="Jugador" />
                                    {/* Agregar balón en tiro a gol */}
                                    <div className='goal__ball__container'>
                                        <img className="goal__ball__container__ball" src="/img/games/runtogoal/balon.png" alt="Balón" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {viewStage === 'KICK_ANIMATION' && (
                    <div className="goal-outro">
                        <img src="/img/games/runtogoal/balon-grande.png" className="goal-outro__ball" alt="Balón" /> {/* Tiro a gol */}
                        {/* <img src={getWinnerImage(winner)} className="goal-outro__ball" alt="Ganador" /> */}
                    </div>
                )}

                {viewStage === 'WINNER_TEXT' && (
                    <div className="goal__pov">
                        {winner ? (
                            <div className="goal__povGoool text-center">
                                {renderWinnerTextScreen(winner)}
                            </div>
                        ) : (
                            // <div className="goal__povGoool text-center">¡CARRERA FINALIZADA!</div>
                            <div className="goal__povGoool text-center">¡GOOOOOL!</div>
                        )}
                        <div className="goal__fw">
                            <div className="goal__fwBurst" />
                            <div className="goal__fwBurst goal__fwBurst--b2" />
                            <div className="goal__fwBurst goal__fwBurst--b3" />
                        </div>
                    </div>
                )}
            </div>

            <footer className='goal__footer'>
                <p className='text-center FooterCredits'>Hecho con ♥ por Interaktia | 2026</p>
            </footer>
        </>
    );
};

export { GoalScreenV2 };
